/**
 * Utilidades compartidas para el manejo de contactos
 * Usadas por header-address.jsx y attendee-address.jsx
 */

/**
 * Extrae el email de un displayName con formatos como:
 * - "Nombre (email@ejemplo.com)"
 * - "Nombre <email@ejemplo.com>"
 * - "email@ejemplo.com"
 */
export const extractEmailFromDisplayName = (displayName) => {
  // Formato: "Nombre (email)"
  const emailMatchParens = displayName.match(/\(([^)]+)\)/);
  if (emailMatchParens) {
    return emailMatchParens[1].trim();
  }
  // Formato: "Nombre <email>"
  const emailMatchAngle = displayName.match(/<([^>]+)>/);
  if (emailMatchAngle) {
    return emailMatchAngle[1].trim();
  }
  // Si contiene @, es solo el email
  if (displayName.includes('@')) {
    return displayName.trim();
  }
  return displayName.trim();
};

/**
 * Extrae el nombre de un displayName con formatos como:
 * - "Nombre (email@ejemplo.com)" -> "Nombre"
 * - "Nombre <email@ejemplo.com>" -> "Nombre"
 * - "Nombre" -> "Nombre"
 */
export const extractNameFromDisplayName = (displayName) => {
  // Formato: "Nombre (email)"
  const matchParens = displayName.match(/^(.+?)\s*\(/);
  if (matchParens) {
    return matchParens[1].replace(/"/g, '').trim();
  }
  // Formato: "Nombre <email>"
  const matchAngle = displayName.match(/^(.+?)\s*</);
  if (matchAngle) {
    return matchAngle[1].replace(/"/g, '').trim();
  }
  // Si no tiene email, el displayName es solo el nombre
  if (!displayName.includes('@')) {
    return displayName.trim();
  }
  return '';
};

/**
 * Filtra contactos por búsqueda en nombre o email
 */
export const filterContactsBySearch = (contacts, searchValue) => {
  const searchLower = searchValue.toLowerCase().trim();
  return contacts.filter(contact => {
    const name = contact.name ? contact.name.toLowerCase() : '';
    const email = contact.email ? contact.email.toLowerCase() : '';
    
    return name.includes(searchLower) || email.includes(searchLower);
  });
};

/**
 * Calcula la relevancia de búsqueda para un contacto
 * Retorna un score basado en coincidencia exacta, prefijo o inclusión
 */
export const calculateSearchRelevance = (contact, searchValue) => {
  const searchLower = searchValue.toLowerCase().trim();
  const name = contact.name ? contact.name.toLowerCase() : '';
  const email = contact.email ? contact.email.toLowerCase() : '';
  
  let score = 0;
  
  if (name === searchLower || email === searchLower) {
    score += 100;
  } else if (name.startsWith(searchLower) || email.startsWith(searchLower)) {
    score += 50;
  } else if (name.includes(searchLower) || email.includes(searchLower)) {
    score += 25;
  }
  
  return score;
};

/**
 * Calcula la relevancia de búsqueda para una cadena de texto (displayName)
 */
export const calculateStringRelevance = (contactString, searchValue) => {
  const searchLower = searchValue.toLowerCase().trim();
  const contactLower = contactString.toLowerCase();
  
  let score = 0;
  
  if (contactLower === searchLower) {
    score += 100;
  } else if (contactLower.startsWith(searchLower)) {
    score += 50;
  } else if (contactLower.includes(searchLower)) {
    score += 25;
  }
  
  return score;
};

/**
 * Elimina contactos duplicados basándose en email Y nombre
 * Solo se considera duplicado si AMBOS coinciden
 * Prioriza contactos de LexOn sobre Graph
 */
export const removeDuplicateContacts = (allContacts) => {
  const duplicateMap = new Map(); // Clave compuesta: "email|name"
  const uniqueContacts = [];

  allContacts.forEach(contact => {
    const email = extractEmailFromDisplayName(contact.displayName);
    const name = extractNameFromDisplayName(contact.displayName);
    const emailKey = email.toLowerCase();
    const nameKey = name.toLowerCase().trim();

    // Crear clave compuesta con email Y nombre
    // Solo se considera duplicado si AMBOS coinciden
    const compositeKey = `${emailKey}|${nameKey}`;

    if (!duplicateMap.has(compositeKey)) {
      // Contacto nuevo (email+nombre únicos), agregar
      duplicateMap.set(compositeKey, contact);
      uniqueContacts.push(contact);
    } else {
      // Ya existe con mismo email Y nombre, verificar si debemos reemplazar
      const existing = duplicateMap.get(compositeKey);
      
      if (contact.isLexon && !existing.isLexon) {
        // Priorizar LexOn sobre Graph
        const index = uniqueContacts.indexOf(existing);
        if (index !== -1) {
          uniqueContacts[index] = contact;
          duplicateMap.set(compositeKey, contact);
        }
      } else if (contact.isLexon === existing.isLexon && 
                 contact.searchRelevance > existing.searchRelevance) {
        // Mismo origen, priorizar mayor relevancia
        const index = uniqueContacts.indexOf(existing);
        if (index !== -1) {
          uniqueContacts[index] = contact;
          duplicateMap.set(compositeKey, contact);
        }
      }
    }
  });

  return uniqueContacts;
};

/**
 * Elimina duplicados de un array de objetos por ID
 */
export const removeDuplicatesById = (arrayOfObjects) => {
  const uniqueIds = [];
  return arrayOfObjects.filter(item => {
    if (!uniqueIds.includes(item.id)) {
      uniqueIds.push(item.id);
      return true;
    }
    return false;
  });
};
